// =============================================================
// VARIANCE — The Executive Command Center
// File      : SalesBookings.tsx
// Location  : /home/Applications/variance/web/src/pages/SalesBookings.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Sales view for Inventory, Bookings, Collections, Add-ons & Additional Works.
// =============================================================

import { useState, useEffect } from 'react';
import { Building2, Plus, X, IndianRupee, Save, Info, Edit, Trash2 } from 'lucide-react';
import { PageHelp, HelpTooltip } from '../components/HelpSystem';

interface Unit {
  id: string; unit_number: string; cluster_name: string;
  sale_area_sqft: number | null; project_name: string;
}

interface Booking {
  id: string; customer_name: string; agreed_value: number;
  booking_date: string; unit_number: string; cluster_name: string;
  project_name: string; project_id: string;
  tax1_name: string; tax1_pct: number;
  tax2_name: string; tax2_pct: number;
  tax3_name: string; tax3_pct: number;
}

interface Collection {
  id: string; booking_id: string; amount: number;
  collection_date: string; mode: string; reference_no: string; collection_name?: string;
}

interface Addon {
  id: string; addon_name: string; amount: number;
}

interface AdditionalWork {
  id: string; booking_id: string; master_work_id: string;
  amount_at_booking: number; gst_pct_at_booking: number; cess_pct_at_booking: number;
  scheduled_date: string; item_name: string; description: string;
}

export function SalesBookings() {
  const [activeTab, setActiveTab] = useState<'inventory' | 'bookings'>('inventory');
  const [units, setUnits] = useState<Unit[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookingCollections, setBookingCollections] = useState<Collection[]>([]);
  const [bookingAddons, setBookingAddons] = useState<Addon[]>([]);
  const [bookingWorks, setBookingWorks] = useState<AdditionalWork[]>([]);
  const [availableWorks, setAvailableWorks] = useState<any[]>([]);

  const [bookingData, setBookingData] = useState({ customer_name: '', agreed_value: '' });
  const [collectionData, setCollectionData] = useState({ amount: '', mode: 'Bank Transfer', reference_no: '', payment_date: new Date().toISOString().split('T')[0] });
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
  const [newAddon, setNewAddon] = useState({ addon_name: '', amount: '' });
  const [newWorkSelection, setNewWorkSelection] = useState({ master_work_id: '', scheduled_date: '' });
  const [taxData, setTaxData] = useState({ tax1_name: 'GST', tax1_pct: 0, tax2_name: 'Cess', tax2_pct: 0, tax3_name: 'Other', tax3_pct: 0 });

  const fetchData = () => {
    setIsLoading(true);
    Promise.all([
      fetch('/api/sales/available-units').then(res => res.json()),
      fetch('/api/sales/bookings').then(res => res.json())
    ]).then(([unitsData, bookingsData]) => {
      if (!unitsData.error) setUnits(unitsData);
      if (!bookingsData.error) setBookings(bookingsData);
      setIsLoading(false);
    }).catch(() => setIsLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const fetchBookingCollections = async (bookingId: string) => {
    const res = await fetch(`/api/sales/bookings/${bookingId}/collections`);
    const data = await res.json();
    if (!data.error) setBookingCollections(data);
  };

  const fetchBookingAddons = async (bookingId: string) => {
    const res = await fetch(`/api/sales/bookings/${bookingId}/addons`);
    const data = await res.json();
    if (!data.error) setBookingAddons(data);
  };

  const fetchBookingWorks = async (bookingId: string) => {
    const res = await fetch(`/api/sales/bookings/${bookingId}/additional-works`);
    const data = await res.json();
    if (!data.error) setBookingWorks(data);
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUnitId) return;
    await fetch('/api/sales/bookings', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        unit_id: selectedUnitId,
        customer_name: bookingData.customer_name,
        agreed_value: parseFloat(bookingData.agreed_value)
      })
    });
    setIsBookingModalOpen(false);
    setBookingData({ customer_name: '', agreed_value: '' });
    setSelectedUnitId('');
    fetchData();
  };

  const handleCollectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingCollectionId
      ? `/api/sales/collections/${editingCollectionId}`
      : '/api/sales/collections';
    const method = editingCollectionId ? 'PATCH' : 'POST';
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_id: selectedBookingId,
        collection_name: collectionData.reference_no || 'Payment',
        due_amount: parseFloat(collectionData.amount),
        received_amount: parseFloat(collectionData.amount),
        bank_name: collectionData.mode,
        status: 'paid'
      })
    });
    setCollectionData({ amount: '', mode: 'Bank Transfer', reference_no: '', payment_date: new Date().toISOString().split('T')[0] });
    setEditingCollectionId(null);
    fetchBookingCollections(selectedBookingId);
  };

  const handleAddonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch(`/api/sales/bookings/${selectedBookingId}/addons`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ addon_name: newAddon.addon_name, amount: parseFloat(newAddon.amount) })
    });
    setNewAddon({ addon_name: '', amount: '' });
    fetchBookingAddons(selectedBookingId);
  };

  const handleDeleteAddon = async (id: string) => {
    if (!window.confirm("Delete this add-on?")) return;
    await fetch(`/api/sales/addons/${id}`, { method: 'DELETE' });
    fetchBookingAddons(selectedBookingId);
  };

  const handleDeleteCollection = async (id: string) => {
    if (!window.confirm("Delete this payment?")) return;
    await fetch(`/api/sales/collections/${id}`, { method: 'DELETE' });
    fetchBookingCollections(selectedBookingId);
  };

  const handleAddWork = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkSelection.master_work_id) return;
    await fetch(`/api/sales/bookings/${selectedBookingId}/additional-works`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newWorkSelection)
    });
    setNewWorkSelection({ master_work_id: '', scheduled_date: '' });
    fetchBookingWorks(selectedBookingId);
  };

  const handleDeleteBookingWork = async (id: string) => {
    if (!window.confirm("Remove this work from booking?")) return;
    await fetch(`/api/sales/booking-addons/${id}`, { method: 'DELETE' });
    fetchBookingWorks(selectedBookingId);
  };

  const handleTaxUpdate = async (field: string, value: any) => {
    const updatedTax = { ...taxData, [field]: value };
    setTaxData(updatedTax);
    await fetch(`/api/sales/bookings/${selectedBookingId}/taxes`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedTax)
    });
    setBookings(prev => prev.map(b => b.id === selectedBookingId ? { ...b, ...updatedTax } : b));
  };

  const openBooking = (unitId: string) => {
    setSelectedUnitId(unitId);
    setIsBookingModalOpen(true);
  };

  const openCollection = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setCollectionData({ amount: '', mode: 'Bank Transfer', reference_no: '', payment_date: new Date().toISOString().split('T')[0] });
    setEditingCollectionId(null);
    setIsCollectionModalOpen(true);
    fetchBookingCollections(bookingId);
    fetchBookingAddons(bookingId);
    fetchBookingWorks(bookingId);

    const b = bookings.find(x => x.id === bookingId);
    if (b) {
      setTaxData({
        tax1_name: b.tax1_name || 'GST', tax1_pct: b.tax1_pct || 0,
        tax2_name: b.tax2_name || 'Cess', tax2_pct: b.tax2_pct || 0,
        tax3_name: b.tax3_name || 'Other', tax3_pct: b.tax3_pct || 0
      });
      
      fetch(`/api/projects/${b.project_id}/additional-works`)
        .then(res => res.json())
        .then(data => { 
          if (!data.error) {
            const matched = data.filter((w: any) => 
              w.cluster_name === b.cluster_name && w.unit_number === b.unit_number
            );
            setAvailableWorks(matched);
          } 
        });
    }
  };

  // Calculations
  const selectedBooking = bookings.find(b => b.id === selectedBookingId);
  const agreedValue = selectedBooking ? Number(selectedBooking.agreed_value) : 0;
  const totalCollected = bookingCollections.reduce((sum, c) => sum + Number(c.received_amount || 0), 0);
  const totalAddons = bookingAddons.reduce((sum, a) => sum + Number(a.amount), 0);
  const baseSubtotal = agreedValue + totalAddons;

  const tax1Amount = baseSubtotal * (Number(taxData.tax1_pct) / 100);
  const tax2Amount = baseSubtotal * (Number(taxData.tax2_pct) / 100);
  const tax3Amount = baseSubtotal * (Number(taxData.tax3_pct) / 100);
  const totalTax = tax1Amount + tax2Amount + tax3Amount;

  const totalAdditionalWorksValue = bookingWorks.reduce((sum, w) => {
    const base = Number(w.amount_at_booking);
    const gst = base * (Number(w.gst_pct_at_booking) / 100);
    const cess = base * (Number(w.cess_pct_at_booking) / 100);
    return sum + base + gst + cess;
  }, 0);

  const grandTotal = baseSubtotal + totalTax + totalAdditionalWorksValue;
  const balanceToPay = grandTotal - totalCollected;

  if (isLoading) return <div className="p-10 text-white">Loading Sales Data...</div>;

  return (
    <div className="space-y-6 max-w-[1600px] relative min-h-screen pt-4">
      <PageHelp
        title="Sales Inventory & Bookings"
        description="Manage unit sales, customer collections, and additional works."
      />

      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Sales Inventory & Bookings</h1>
          <p className="text-sm text-gray-400 mt-1">Manage unit sales and customer collections.</p>
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center justify-between border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <div className="flex">
          <button onClick={() => setActiveTab('inventory')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'inventory' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Available Inventory ({units.length})</button>
          <button onClick={() => setActiveTab('bookings')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'bookings' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>Active Bookings ({bookings.length})</button>
        </div>
      </div>

      {/* INVENTORY TAB */}
      {activeTab === 'inventory' && (
        <div className="rounded-xl overflow-hidden border min-h-[200px]" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Project</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Cluster</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Unit No.</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Area (Sft)</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {units.map(unit => (
                <tr key={unit.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{unit.project_name}</td>
                  <td className="p-4 text-gray-300">{unit.cluster_name}</td>
                  <td className="p-4 text-gray-300 font-mono">{unit.unit_number}</td>
                  <td className="p-4 text-right text-gray-300 font-mono">{unit.sale_area_sqft || '-'}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => openBooking(unit.id)} className="px-3 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Book Unit</button>
                  </td>
                </tr>
              ))}
              {units.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-500">No available units. Add inventory in the Projects module first.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* BOOKINGS TAB */}
      {activeTab === 'bookings' && (
        <div className="rounded-xl overflow-hidden border min-h-[200px]" style={{ backgroundColor: 'var(--bg-panel)', borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Customer</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400">Unit</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-right">Agreed Value</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Date</th>
                <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => (
                <tr key={b.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 text-white font-medium">{b.customer_name}</td>
                  <td className="p-4 text-gray-300">{b.project_name} - {b.cluster_name} - {b.unit_number}</td>
                  <td className="p-4 text-right text-gray-300 font-mono">₹{Number(b.agreed_value).toLocaleString('en-IN')}</td>
                  <td className="p-4 text-center text-gray-400 text-sm">{b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-IN') : '-'}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => openCollection(b.id)} className="px-3 py-1 rounded text-xs font-bold text-white" style={{ backgroundColor: '#22C55E' }}>Record Payment</button>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-gray-500">No active bookings found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* BOOKING SLIDE-OUT PANEL */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsBookingModalOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--brand-accent)]">Sales Action</span>
                <h2 className="text-xl font-bold text-white mt-1">Book Unit</h2>
              </div>
              <button onClick={() => setIsBookingModalOpen(false)} className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-canvas)] transition-colors"><X size={24} /></button>
            </div>
            <form onSubmit={handleBookingSubmit} className="p-6 space-y-6 flex-1 overflow-y-auto">
              <div className="mb-4 p-3 rounded-lg bg-[var(--bg-panel-alt)] border border-[var(--border-subtle)] flex items-center gap-2 text-xs text-gray-400">
                <Info size={16} className="text-[var(--brand-accent)] shrink-0" />
                <span>This will lock the unit and mark it as <strong>Booked</strong> in inventory.</span>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Customer Name <HelpTooltip text="Full legal name of the primary buyer." /></label>
                <input required type="text" value={bookingData.customer_name} onChange={e => setBookingData({...bookingData, customer_name: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[var(--brand-accent)] outline-none" />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Agreed Value (₹) <HelpTooltip text="Total sale value agreed with the customer." /></label>
                <input required type="number" value={bookingData.agreed_value} onChange={e => setBookingData({...bookingData, agreed_value: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[var(--brand-accent)] outline-none" />
              </div>
              <div className="pt-6 border-t border-[var(--border-subtle)]">
                <button type="submit" className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-white shadow-lg" style={{ backgroundColor: 'var(--brand-accent)' }}><Save size={18} /> Confirm Booking</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* COLLECTION SLIDE-OUT PANEL */}
      {isCollectionModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="flex-1" onClick={() => setIsCollectionModalOpen(false)}></div>
          <div className="w-full md:max-w-md h-full bg-[var(--bg-panel)] shadow-2xl border-l border-[var(--border-subtle)] flex flex-col animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-[var(--border-subtle)] flex justify-between items-start bg-[var(--bg-panel-alt)]">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#22C55E]">Finance Action</span>
                <h2 className="text-xl font-bold text-white mt-1">Record Collection</h2>
              </div>
              <button onClick={() => setIsCollectionModalOpen(false)} className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-[var(--bg-canvas)] transition-colors"><X size={24} /></button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-6">
              {/* BOOKING SUMMARY */}
              <div className="p-4 rounded-xl bg-[var(--bg-panel-alt)] border border-[var(--border-subtle)]">
                <div className="flex justify-between text-sm mb-1 text-gray-400"><span>Base Agreed Value</span><span className="font-mono text-white">₹{agreedValue.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm mb-1 text-blue-400"><span>Add-ons</span><span className="font-mono">+ ₹{totalAddons.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm mb-1 text-orange-400"><span>Base Tax ({taxData.tax1_name} {taxData.tax1_pct}%)</span><span className="font-mono">₹{totalTax.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm mb-1 text-purple-400"><span>Additional Works</span><span className="font-mono">+ ₹{totalAdditionalWorksValue.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm font-bold text-white border-t border-[var(--border-subtle)] pt-2 mt-2"><span>Grand Total</span><span className="font-mono">₹{grandTotal.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm mb-1 text-green-500 mt-1"><span>Collected</span><span className="font-mono">- ₹{totalCollected.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm font-bold text-red-400 border-t border-[var(--border-subtle)] pt-2 mt-2"><span>Balance to Pay</span><span className="font-mono">₹{balanceToPay.toLocaleString('en-IN')}</span></div>
              </div>

              {/* TAX CONFIGURATION */}
              <div className="border border-[var(--border-subtle)] rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3">Tax Configuration</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 1 Name</label>
                    <input type="text" value={taxData.tax1_name} onChange={e => handleTaxUpdate('tax1_name', e.target.value)} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 1 Rate (%)</label>
                    <input type="number" value={taxData.tax1_pct} onChange={e => handleTaxUpdate('tax1_pct', Number(e.target.value))} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 2 Name</label>
                    <input type="text" value={taxData.tax2_name} onChange={e => handleTaxUpdate('tax2_name', e.target.value)} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 2 Rate (%)</label>
                    <input type="number" value={taxData.tax2_pct} onChange={e => handleTaxUpdate('tax2_pct', Number(e.target.value))} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 3 Name</label>
                    <input type="text" value={taxData.tax3_name} onChange={e => handleTaxUpdate('tax3_name', e.target.value)} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Tax 3 Rate (%)</label>
                    <input type="number" value={taxData.tax3_pct} onChange={e => handleTaxUpdate('tax3_pct', Number(e.target.value))} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  </div>
                </div>
              </div>

              {/* ADDITIONAL WORKS */}
              <div>
                <h3 className="text-sm font-bold text-gray-300 mb-3">Additional Works Requested</h3>
                <div className="space-y-2 mb-4">
                  {bookingWorks.map(w => (
                    <div key={w.id} className="flex justify-between items-center p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)]">
                      <div>
                        <div className="text-sm font-bold text-white">{w.item_name}</div>
                        <div className="text-xs text-purple-400 font-mono">₹{Number(w.amount_at_booking).toLocaleString('en-IN')} + GST {w.gst_pct_at_booking}% + Other {w.cess_pct_at_booking}%</div>
                        {w.scheduled_date && <div className="text-xs text-gray-500">Scheduled: {new Date(w.scheduled_date).toLocaleDateString('en-IN')}</div>}
                      </div>
                      <button onClick={() => handleDeleteBookingWork(w.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  {bookingWorks.length === 0 && <div className="text-xs text-gray-500 text-center py-2">No additional works requested.</div>}
                </div>
                <form onSubmit={handleAddWork} className="space-y-2">
                  <select required value={newWorkSelection.master_work_id} onChange={e => setNewWorkSelection({...newWorkSelection, master_work_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm">
                    <option value="">-- Select Additional Work --</option>
                    {availableWorks.map(w => (
                      <option key={w.id} value={w.id}>{w.item_name} (₹{Number(w.amount).toLocaleString('en-IN')})</option>
                    ))}
                  </select>
                  <input type="date" value={newWorkSelection.scheduled_date} onChange={e => setNewWorkSelection({...newWorkSelection, scheduled_date: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white text-sm" />
                  <button type="submit" className="w-full py-2 rounded text-white text-sm font-bold" style={{ backgroundColor: 'var(--brand-accent)' }}>+ Opt-In This Work</button>
                </form>
              </div>

              {/* PAYMENT FORM */}
              <form onSubmit={handleCollectionSubmit} className="space-y-4 bg-[var(--bg-panel-alt)] p-4 rounded-xl border border-[var(--border-subtle)] mt-4">
                <h3 className="text-sm font-bold text-white mb-2">{editingCollectionId ? 'Edit Payment' : 'Add New Payment'}</h3>
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Amount (₹)</label>
                  <input required type="number" value={collectionData.amount} onChange={e => setCollectionData({...collectionData, amount: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[#22C55E] outline-none" />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Payment Mode</label>
                  <select value={collectionData.mode} onChange={e => setCollectionData({...collectionData, mode: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[#22C55E] outline-none">
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Reference No.</label>
                  <input type="text" value={collectionData.reference_no} onChange={e => setCollectionData({...collectionData, reference_no: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[#22C55E] outline-none" />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm mb-2 font-medium text-gray-300">Payment Date</label>
                  <input type="date" value={collectionData.payment_date} onChange={e => setCollectionData({...collectionData, payment_date: e.target.value})} className="w-full p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white focus:border-[#22C55E] outline-none" />
                </div>
                <button type="submit" className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-white shadow-lg" style={{ backgroundColor: '#22C55E' }}>
                  <Save size={18} /> {editingCollectionId ? 'Update Payment' : 'Save Payment'}
                </button>
              </form>

              {/* PAYMENT HISTORY */}
              <div>
                <h3 className="text-sm font-bold text-gray-300 mb-3">Payment History</h3>
                <div className="space-y-2">
                  {bookingCollections.map(c => (
                    <div key={c.id} className="flex justify-between items-center p-3 rounded-lg bg-[var(--bg-canvas)] border border-[var(--border-subtle)]">
                      <div>
                        <div className="text-sm font-bold text-white">₹{Number(c.due_amount || c.amount).toLocaleString('en-IN')}</div>
                        <div className="text-xs text-gray-400">{c.bank_name || c.mode} | {c.collection_name || c.reference_no || 'No Ref'}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => {
                          setEditingCollectionId(c.id);
                          setCollectionData({ amount: String(c.received_amount || c.amount || ''), mode: c.bank_name || c.mode || 'Bank Transfer', reference_no: c.collection_name || c.reference_no || '', payment_date: new Date().toISOString().split('T')[0] });
                        }} className="p-1 text-blue-400 hover:text-blue-300"><Edit size={16} /></button>
                        <button onClick={() => handleDeleteCollection(c.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                  {bookingCollections.length === 0 && <div className="text-xs text-gray-500 text-center py-4">No payments recorded yet.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
